"use client";

import { useState } from "react";
import { FolderPlus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PermissionGate } from "@/components/shared/permission-gate";
import type { CommandProjectVisibility } from "@/types/command-agent";

interface CreateCommandProjectPopoverProps {
  onCreate: (input: {
    name: string;
    visibility: CommandProjectVisibility;
  }) => void;
  isCreating?: boolean;
}

export function CreateCommandProjectPopover({
  onCreate,
  isCreating,
}: CreateCommandProjectPopoverProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [visibility, setVisibility] =
    useState<CommandProjectVisibility>("PERSONAL");

  const submit = () => {
    const trimmed = name.trim();
    if (trimmed.length < 2 || isCreating) return;
    onCreate({ name: trimmed, visibility });
    setName("");
    setVisibility("PERSONAL");
    setOpen(false);
  };

  return (
    <PermissionGate requiredPermissions={["CREATE_COMMAND_PROJECT"]}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="size-5 p-0 text-muted-foreground hover:text-foreground"
          title="New project"
          type="button"
        >
          <FolderPlus className="size-3.5" />
        </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 space-y-3 p-3">
          <div>
            <p className="text-sm font-semibold">New project</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Group related chats into one folder.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="project-name" className="text-xs">
              Name
            </Label>
            <Input
              id="project-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") submit();
              }}
              placeholder="Project name"
              className="h-8 text-sm"
              maxLength={100}
              autoFocus
              disabled={isCreating}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Visibility</Label>
            <Select
              value={visibility}
              onValueChange={(value) =>
                setVisibility(value as CommandProjectVisibility)
              }
              disabled={isCreating}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PERSONAL">Personal</SelectItem>
                <SelectItem value="TEAM">Team</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={() => setOpen(false)}
              disabled={isCreating}
              type="button"
            >
              Cancel
            </Button>
            <Button
              className="h-7 px-3 text-xs"
              disabled={name.trim().length < 2 || isCreating}
              onClick={submit}
              type="button"
            >
              {isCreating ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                "Create"
              )}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </PermissionGate>
  );
}
